import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import {
  cloneDeep,
  forEach,
  isEmpty,
  isEqual,
  set,
  sortBy,
  uniqBy,
} from 'lodash';
import {
  CLIENT_TYPE,
  FinanceApplicationStatus,
  FinanceProvider,
  FinanceResponseStatus,
  FinanceType,
} from '@upstart/harmony/lib/constants';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import {
  FinanceApplication,
  FinanceResponse,
} from '@upstart/harmony/lib/legos/financingTypes';
import { getUserMessage } from '@upstart/harmony/lib/error-utils';
import { getImageForFinanceSource } from '@upstart/harmony/lib/metadata';
import { RouteOneFinanceSource } from '@upstart/harmony/lib/legos/routeOneTypes';
import {
  PlaceholderTile,
  BaseTile,
  DeniedTile,
} from '@upstart/desking-components';
import {
  sendData,
  getCarInfo,
  getUpstartOfferEventProperties,
} from 'util/amplitude';
import {
  amplitudeEvents,
  amplitudeEventsDeskingCTR,
} from 'util/amplitudeEvents';
import { isPresent } from 'util/typeUtils';
import { Feature, useGrowthBook } from 'hooks/GrowthBook';
import FinanceApplicationService from '../../services/FinanceApplicationService';
import LendingOffer from './LendingOffer';
import LendingOfferDetail from './LendingOfferDetail';
import CustomerSelectedOffer from './CustomerSelectedOffer/CustomerSelectedOffer';
import './LendingOffers.scss';
import LoadingOverlay from '../../components/LoadingOverlay';
import FinanceSourcesModal from '../../components/PencilEditor/FinanceSourcesModal/FinanceSourcesModal';
import useCloneFinanceResponse from '../../components/PencilEditor/useCloneFinanceResponse';
import { isDuplicateUpstartOfferLimitReached } from '../../util/financeResponseUtils';
import useUpstartPlaceholderTile from '../../hooks/useUpstartPlaceholderTile';
import { subscribeToWebsocketRicSignedEvent } from './WebsocketSubscription';

const TILE_HEIGHT = 117;

interface Props {
  car?: Car;
  deal?: Deal;
  dealer?: DealerWithFlags;
  pencil: Pencil;
  updatePencil: (pencil: Pencil) => void;
  updateFinanceApplicationError: (msg: string | null) => void;
  selectedFinanceApplication: FinanceApplication | null;
  showRicTab: boolean;
  setShowRicTab: (isShown: boolean) => void;
  showCustomerSelectedOffer: boolean;
  setShowCustomerSelectedOffer: (isShown: boolean) => void;
  fetchedApplications?: FinanceApplication[] | null;
  fetchedResponses?: FinanceResponse[] | null;
  showLendingOffers: boolean;
  forwardDealToFNI: (payload: {
    dealId: string;
    submitToLenders: boolean;
    sendToUpstart: boolean;
    skipActiveOfferInvalidation: boolean;
    selectedLenderIds?: string[];
  }) => void;
  fetchFinanceResponses: VoidFunction;
}

const LendingOffers = ({
  car,
  deal,
  dealer,
  pencil,
  updatePencil,
  updateFinanceApplicationError,
  showLendingOffers,
  selectedFinanceApplication,
  showRicTab,
  setShowRicTab,
  showCustomerSelectedOffer,
  setShowCustomerSelectedOffer,
  fetchedApplications,
  fetchedResponses,
  forwardDealToFNI,
  fetchFinanceResponses,
}: Props) => {
  const [financeApplications, setFinanceApplications] = useState<
    FinanceApplication[] | null | undefined
  >(fetchedApplications);
  const [financeResponses, setFinanceResponses] = useState<
    FinanceResponse[] | null | undefined
  >(fetchedResponses);
  const [
    upstartIneligibleTileApplication,
    setUpstartIneligibleTileApplication,
  ] = useState<FinanceApplication | null | undefined>(null);
  const [detailViewOffer, setDetailViewOffer] = useState<
    FinanceResponse | null | undefined
  >(null);
  const [offerSelectionChanged, setOfferSelectionChanged] = useState(false);
  const [fniSubmissionLoading, setFniSubmissionLoading] = useState(false);
  const [
    showFinanceSourceSubmissionModal,
    setShowFinanceSourceSubmissionModal,
  ] = useState(false);
  const { asyncCloneFinanceResponse } = useCloneFinanceResponse();
  const fetchApplicationStatuses = useAsyncCallback(
    FinanceApplicationService.getApplicationStatuses,
  );

  const executeFetchApplicationStatuses = () => {
    const providerIds =
      financeApplications
        ?.map(a =>
          a.provider === FinanceProvider.UPSTART
            ? a.providerApplicationId
            : null,
        )
        .filter(isPresent) ?? [];

    if (providerIds.length) {
      fetchApplicationStatuses.execute(providerIds);
    }
  };

  // Update finance application statuses from dapi when finance applications and/or response change
  // The locally stored status has the potential to be very out of date with the actual dapi status.
  // We need to fetch these statuses more frequently to ensure the correct data is presented to the user
  useEffect(() => {
    executeFetchApplicationStatuses();
  }, [financeApplications, financeResponses]);

  // Listen for RIC signed event to re-fetch application statuses
  useEffect(() => {
    if (!deal?.id) {
      return () => {};
    }
    const unsubscribe = subscribeToWebsocketRicSignedEvent(
      deal.id,
      async () => {
        executeFetchApplicationStatuses();
      },
    );
    return () => unsubscribe();
  }, [deal?.id]);

  const {
    showPlaceholderTile,
    placeholderRowOneText,
    placeholderRowTwoText,
  } = useUpstartPlaceholderTile({
    financeResponses,
    financeApplications,
  });

  const submitToSelectedLenders = !!dealer?.lending?.instore
    ?.submitToSelectedLenders;
  // Extract existing finance applications from pencil
  const pencilApplications: FinanceApplication[] =
    (pencil as any).financeApplications ??
    (pencil as any).data?.financeApplications ??
    [];

  // Check if "No offers" option is selected
  const markedNoOffers =
    pencilApplications.some(a => (a as any).markNoOffers) ||
    (pencilApplications.length === 0 && !!pencil.data?.sendWithNoOffers);

  // Sort responses by upstart offers first and then by timestamps
  const responses = sortBy(
    financeResponses ?? [],
    fr =>
      financeApplications?.find(a => a.id === fr.applicationId)?.provider !==
      FinanceProvider.UPSTART,
    'createdAt',
  );

  const duplicateUpstartOfferLimitReached = isDuplicateUpstartOfferLimitReached(
    financeApplications ?? [],
    financeResponses ?? [],
  );

  // Sort pencil responses by their timestamp
  const pencilResponses = sortBy(
    pencilApplications.reduce(
      (acc, a) => acc.concat((a as any).responses || []),
      [],
    ),
    'createdAt',
  ) as FinanceResponse[];

  useEffect(() => {
    sendData(
      amplitudeEvents.DESKING_PENCIL_TAB_FINANCEOFFERS_START,
      getCarInfo(car),
    );
  }, []);

  const setPencilData = useCallback(
    (
      newResponses: FinanceResponse[],
      // Flag to explicitly tell client to render "No response" message
      markNoOffers = markedNoOffers,
    ) => {
      // Copy pencil
      const newPencil = cloneDeep(pencil);

      // Generate new application list
      const updatedApplications = (financeApplications || []).map(a => ({
        ...a,
        markNoOffers,
        responses: newResponses.filter(r => r.applicationId === a.id),
      }));
      set(newPencil, 'financeApplications', updatedApplications);

      if (updatedApplications.length === 0) {
        set(newPencil, 'data.sendWithNoOffers', true);
      }

      // Update based on deep compare to avoid infinite loop.
      if (!isEqual(pencil, newPencil)) {
        updatePencil(newPencil);
      }
    },
    [markedNoOffers, pencil, financeApplications, updatePencil],
  );

  const setResponse = (newResponse: FinanceResponse) => {
    setOfferSelectionChanged(true);
    // Merge response to existing ones
    setPencilData(uniqBy([newResponse, ...pencilResponses], 'id'), false);
  };

  let approvedResponses = responses.filter(
    response =>
      response.status !== FinanceResponseStatus.ERROR &&
      response.status !== FinanceResponseStatus.NONE,
  );
  if (!dealer?.flags.showCounterOffer) {
    approvedResponses = approvedResponses.filter(
      response => response.status !== FinanceResponseStatus.COUNTERED,
    );
  }

  const activeApplicationIsCreditFirst = financeApplications?.find(
    f =>
      f.status === FinanceApplicationStatus.ACTIVE && f.creditFirstSubmission,
  );

  const canDoManualSubmission =
    dealer?.fniType && !!dealer.lending?.instore?.manualFISubmission;
  const growthbook = useGrowthBook();
  const shouldShowManualSubmissionButton =
    growthbook.getFeatureValue(Feature.RTA_CREDIT_FIRST_ENHANCEMENTS, false) &&
    activeApplicationIsCreditFirst
      ? canDoManualSubmission
      : canDoManualSubmission &&
        financeApplications?.filter(
          application => application.provider !== FinanceProvider.UPSTART,
        ).length === 0 &&
        !activeApplicationIsCreditFirst;

  const forceExpandOfferList = () => {
    setOfferSelectionChanged(true);
    setShowCustomerSelectedOffer(false);
  };

  const manualFniSubmit = async (selectedLenders?: RouteOneFinanceSource[]) => {
    setFniSubmissionLoading(true);

    if (deal?.id) {
      forwardDealToFNI({
        dealId: deal.id,
        submitToLenders: true,
        sendToUpstart: false,
        skipActiveOfferInvalidation: true,
        selectedLenderIds: selectedLenders?.map(lender => lender.fsId),
      });
    }
    setShowFinanceSourceSubmissionModal(false);
  };

  // Upstart Application handler
  useEffect(() => {
    updateFinanceApplicationError(null);
    if (financeApplications?.length) {
      forEach(financeApplications, a => {
        if (
          a.provider === FinanceProvider.UPSTART &&
          a.status === FinanceApplicationStatus.FAILED &&
          !isEmpty(a?.response?.errors?.[0])
        ) {
          updateFinanceApplicationError(
            `Credit Application Error: ${getUserMessage(
              a.response?.errors?.[0] || '',
            )}`,
          );
          setUpstartIneligibleTileApplication(a);
          return false;
        }
        return true;
      });
    }
    // eslint-disable-next-line
  }, [financeApplications]);

  useEffect(() => {
    // Clear the loading tile as soon as there is a non-upstart response
    if (
      financeResponses?.some(
        response =>
          financeApplications?.find(
            application => application.id === response.applicationId,
          )?.provider !== FinanceProvider.UPSTART,
      )
    ) {
      setFniSubmissionLoading(false);
    }
  }, [financeResponses]);

  // Merge any new responses to existing pencil data
  useEffect(() => {
    if (financeResponses) {
      const newResponses = [...pencilResponses];
      for (const response of financeResponses) {
        const application = financeApplications?.find(
          a => a.id === response.applicationId,
        );
        if (!newResponses.some(r => r.id === response.id)) {
          // Preselect responses to Online applications unless desking manager explicitly selected "No Offers Available".
          newResponses.push({
            ...response,
            selected:
              !markedNoOffers && application?.clientType === CLIENT_TYPE.ONLINE,
          } as FinanceResponse);
        }
      }
      setPencilData(newResponses, markedNoOffers);
    }
  }, [
    financeResponses,
    financeApplications,
    markedNoOffers,
    pencilResponses,
    setPencilData,
  ]);

  useEffect(() => {
    if (detailViewOffer) {
      // reset the detailViewOffer if the user on papyrus changes the payment option and no application result so the chairman user will not see the detailViewOffer without selecting any offer
      setDetailViewOffer(null);
    }
    // eslint-disable-next-line
  }, [financeApplications]);

  useEffect(() => {
    setFinanceApplications(fetchedApplications);
  }, [fetchedApplications]);

  useEffect(() => {
    setFinanceResponses(fetchedResponses);
  }, [fetchedResponses]);

  // Use a reference to avoid depending on detailViewOffer being nullable which
  // indicates the No Offers tile is selected
  const shouldAutoSelectOffer = useRef(!selectedFinanceApplication);

  // If the user selects an offer, disable auto-selecting an offer
  useEffect(() => {
    if (detailViewOffer) {
      shouldAutoSelectOffer.current = false;
    }
  }, [detailViewOffer]);

  // Once Upstart lending is done loading, set the selected offer to the first
  // Upstart offer as long as another offer hasn't already been selected
  useEffect(() => {
    const hasUpstartApplication = financeApplications?.some(
      a => a.provider === FinanceProvider.UPSTART,
    );
    if (
      shouldAutoSelectOffer.current &&
      approvedResponses.length &&
      (!hasUpstartApplication || !showPlaceholderTile)
    ) {
      shouldAutoSelectOffer.current = false;
      const firstResponse = approvedResponses[0];
      if (firstResponse) {
        setDetailViewOffer(firstResponse);
      }
    }
  }, [
    financeApplications?.length,
    approvedResponses.length,
    showPlaceholderTile,
  ]);

  // If the customer has selected an offer, show the selected offer
  useEffect(() => {
    if (
      showCustomerSelectedOffer &&
      selectedFinanceApplication &&
      pencilResponses &&
      responses?.length > 0
    ) {
      const selectedResponse = approvedResponses.find(
        r => r.id === deal?.selectedFinanceResponseId,
      );

      if (selectedResponse) {
        setDetailViewOffer(
          pencilResponses.find(pr => pr.id === selectedResponse.id),
        );
      }
    }
  }, [
    showCustomerSelectedOffer,
    selectedFinanceApplication,
    pencilResponses,
    responses,
  ]);

  useEffect(() => {
    // Offer selections for customer changed. This should not affect the status of showCustomerSelectedOffer.
    if (!offerSelectionChanged) {
      setShowCustomerSelectedOffer(Boolean(selectedFinanceApplication));
    }
    // eslint-disable-next-line
  }, [selectedFinanceApplication]);

  // Update finance response list state immediately upon receiving clone finance response result
  useEffect(() => {
    if (asyncCloneFinanceResponse.result) {
      setFinanceResponses([
        ...(financeResponses ?? []),
        asyncCloneFinanceResponse.result,
      ]);
    }
  }, [asyncCloneFinanceResponse.result]);

  const getApplicationForResponse = (responseId: string) =>
    financeApplications?.find(
      a => a.id === responses.find(r => r.id === responseId)?.applicationId,
    );

  const getApplicationStatusForResponse = (responseId: string) => {
    const application = getApplicationForResponse(responseId);
    return fetchApplicationStatuses.result?.find(
      s => s.applicationId === application?.providerApplicationId,
    );
  };

  // Do not render anything if there are no active applications and we are not showing the manual submission button
  if (!shouldShowManualSubmissionButton && !deal?.submittedToLendersAt) {
    if (!financeApplications?.length && showLendingOffers)
      return (
        <div className="options-changed-alert">
          Payment options have been changed by the salesperson, no offers
          available.
        </div>
      );
    if (!financeApplications?.length || !showLendingOffers) {
      return null;
    }
  }

  const renderOffers = () => (
    <>
      <div className="offers-header">
        <h3 className="title">{approvedResponses.length} Finance Offers</h3>
        {selectedFinanceApplication && (
          <div
            className="collapse-expand-offers-btn"
            onClick={() => setShowCustomerSelectedOffer(true)}
          >
            Collapse and view selected offer
          </div>
        )}
      </div>
      <div className="lending-offers-row">
        <BaseTile
          header={<b className="no-offer-text">Send &quot;No Offers&quot;</b>}
          body={
            <div className="no-offer-body">
              This option will not send any offers
            </div>
          }
          checked={markedNoOffers}
          onCheckboxToggle={() => {
            setPencilData(
              pencilResponses.map(r => ({ ...r, selected: false })),
              !markedNoOffers,
            );
          }}
          onClick={() => {
            setDetailViewOffer(null);
          }}
          isSelected={detailViewOffer === null}
          height={TILE_HEIGHT}
        />

        {showPlaceholderTile && (
          <PlaceholderTile
            financeSourceName={FinanceProvider.UPSTART}
            financeImageUrl={getImageForFinanceSource(FinanceProvider.UPSTART)}
            rowOne={placeholderRowOneText}
            rowTwo={placeholderRowTwoText}
            height={TILE_HEIGHT}
          />
        )}
        {upstartIneligibleTileApplication && (
          <DeniedTile
            financeSourceName={FinanceProvider.UPSTART}
            financeImageUrl={getImageForFinanceSource(FinanceProvider.UPSTART)}
            height={TILE_HEIGHT}
            onClick={() => {
              sendData(
                amplitudeEventsDeskingCTR.FINANCE_OFFER_TILE_CLICKED,
                getUpstartOfferEventProperties({
                  dealId: deal?.id,
                  application: upstartIneligibleTileApplication,
                }),
              );
            }}
          />
        )}
        {approvedResponses.map(response => (
          <LendingOffer
            key={response.id}
            response={response}
            pencilResponse={pencilResponses.find(pr => pr.id === response.id)}
            setResponse={setResponse}
            application={getApplicationForResponse(response.id)}
            applicationStatus={getApplicationStatusForResponse(response.id)}
            setDetailViewOffer={setDetailViewOffer}
            detailViewOffer={detailViewOffer}
            showUpstartPlaceholder={showPlaceholderTile}
            deal={deal}
            dealer={dealer}
            asyncCloneFinanceResponse={asyncCloneFinanceResponse}
            duplicateUpstartOfferLimitReached={
              duplicateUpstartOfferLimitReached
            }
            forceExpandOfferList={forceExpandOfferList}
            height={TILE_HEIGHT}
          />
        ))}
        {shouldShowManualSubmissionButton ? (
          <div className="manual-fni-submit">
            <LoadingOverlay active={fniSubmissionLoading}>
              <button
                type="button"
                className="manual-fni-submit-btn"
                onClick={() =>
                  submitToSelectedLenders
                    ? setShowFinanceSourceSubmissionModal(true)
                    : manualFniSubmit()
                }
              >
                Send to {dealer?.fniType}
              </button>
            </LoadingOverlay>
          </div>
        ) : (
          fniSubmissionLoading && (
            <PlaceholderTile
              financeSourceName={dealer?.fniType as FinanceProvider}
              height={TILE_HEIGHT}
            />
          )
        )}
      </div>
    </>
  );

  const selectedResponse = approvedResponses.find(
    r => r.id === deal?.selectedFinanceResponseId,
  );

  const renderCustomerSelectedOffer = () => {
    if (!selectedResponse) {
      return null;
    }
    const offerCount = responses.filter(
      r =>
        r.status !== FinanceResponseStatus.ERROR &&
        r.status !== FinanceResponseStatus.NONE,
    ).length;

    return (
      <CustomerSelectedOffer
        offerCount={offerCount}
        onViewMoreOffer={() => setShowCustomerSelectedOffer(false)}
      >
        <LendingOffer
          key={selectedResponse.id}
          response={selectedResponse}
          pencilResponse={pencilResponses.find(
            pr => pr.id === selectedResponse?.id,
          )}
          setResponse={setResponse}
          application={getApplicationForResponse(selectedResponse.id)}
          applicationStatus={getApplicationStatusForResponse(
            selectedResponse.id,
          )}
          setDetailViewOffer={setDetailViewOffer}
          detailViewOffer={detailViewOffer}
          dealer={dealer}
          asyncCloneFinanceResponse={asyncCloneFinanceResponse}
          duplicateUpstartOfferLimitReached={duplicateUpstartOfferLimitReached}
          forceExpandOfferList={forceExpandOfferList}
          height={TILE_HEIGHT}
        />
      </CustomerSelectedOffer>
    );
  };

  return (
    <div className="data-table-container lending-offers">
      <div className="soft-credit-tab-wrapper">
        {dealer?.featureConfig?.softCreditPull?.inStoreCreditApp && (
          <div className="soft-credit-tab">
            Soft pull through 700credit:{' '}
            {deal?.creditPull?.creditTier?.score ?? ' N/A '}
          </div>
        )}
      </div>
      <div className="offers-wrapper">
        {showCustomerSelectedOffer &&
        selectedFinanceApplication &&
        selectedResponse
          ? renderCustomerSelectedOffer()
          : renderOffers()}
      </div>
      {submitToSelectedLenders && (
        <FinanceSourcesModal
          dealer={dealer}
          isOpen={showFinanceSourceSubmissionModal}
          onClose={() => setShowFinanceSourceSubmissionModal(false)}
          onSubmit={(selectedLenderIds: RouteOneFinanceSource[]) =>
            manualFniSubmit(selectedLenderIds)
          }
          financeType={deal?.financeType ?? FinanceType.PURCHASE}
        />
      )}
      {detailViewOffer !== undefined && car && deal && dealer && (
        <LendingOfferDetail
          deal={deal}
          response={
            detailViewOffer
              ? approvedResponses.find(ar => ar.id === detailViewOffer.id) ??
                null
              : null
          }
          pencil={pencil}
          dealer={dealer}
          pencilResponse={
            detailViewOffer
              ? pencilResponses.find(pr => pr.id === detailViewOffer.id) ?? null
              : null
          }
          application={
            detailViewOffer
              ? getApplicationForResponse(detailViewOffer.id) ?? null
              : null
          }
          applicationStatus={
            detailViewOffer
              ? getApplicationStatusForResponse(detailViewOffer.id) ?? null
              : null
          }
          car={car}
          setResponse={setResponse}
          reloadResponses={() => fetchFinanceResponses()}
          showRicTab={showRicTab}
          setShowRicTab={setShowRicTab}
        />
      )}
    </div>
  );
};

LendingOffers.propTypes = {
  car: PropTypes.object,
  deal: PropTypes.object,
  dealer: PropTypes.object,
  pencil: PropTypes.object,
  updatePencil: PropTypes.func,
  updateFinanceApplicationError: PropTypes.func,
  selectedFinanceApplication: PropTypes.object,
  showRicTab: PropTypes.bool,
  setShowRicTab: PropTypes.func,
  showCustomerSelectedOffer: PropTypes.bool,
  setShowCustomerSelectedOffer: PropTypes.func,
  fetchedApplications: PropTypes.array,
  fetchedResponses: PropTypes.array,
  showLendingOffers: PropTypes.bool,
};

export default LendingOffers;
